import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import BlogPublishInfo from '../BlogPublishInfo.tsx';
import eventBusDesignPatternPng from "../../static/eventbus_design_pattern_main_image.png";
import ImageListItem from '@mui/material/ImageListItem';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { CodeBlock, dracula } from 'react-code-blocks';

const codeTextPublish = `
// Load the AWS SDK for Node.js
var AWS = require("aws-sdk");
// Set region
AWS.config.update({ region: "REGION" });

// Create publish parameters
var params = {
  Message: "DATA_TO_BE_USED_BY_SERVICE_02",
  TopicArn: "TOPIC_ORDER_PROCESS",
};

// Create promise and SNS service object
var publishTextPromise = new AWS.SNS({ apiVersion: "STABLE_VERSION_HERE" })
  .publish(params)
  .promise();

// Handle promise's fulfilled/rejected states
publishTextPromise
  .then(function (data) {
    console.log(
      \`Message \${params.Message} sent to the topic \${params.TopicArn}\`
    );
  })
  .catch(function (err) {
    console.error(err, err.stack);
  });

`;

const codeTextSubscribe = `
// Load the AWS SDK for Node.js
var AWS = require("aws-sdk");
// Set region
AWS.config.update({ region: "REGION" });

const params = {
  TopicArn: "TOPIC_ORDER_PROCESS",
};

// Create promise and SNS service object
var subslistPromise = new AWS.SNS({ apiVersion: "STABLE_VERSION_HERE" })
  .listSubscriptionsByTopic(params)
  .promise();

// Handle promise's fulfilled/rejected states
subslistPromise
  .then(function (data) {
    console.log(data);
  })
  .catch(function (err) {
    console.error(err, err.stack);
  });
`;
export default function EventBusDesignPatternBlog02() {
    return (
        <Container id="EventBusDesignPatternBlog02" sx={{ my: { sm: 0, xs: 5 }}} fixed>
          <Box sx={{  minHeight: '50vh' }}>
              <Typography variant="h1" component="h1" align="center">
                Event Bus Design Pattern in Node.js Microservices
              </Typography>
              <BlogPublishInfo date="27th Jan 2025"/>
              <Divider />
              <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '0vw'} }}>
                <img
                    srcSet={eventBusDesignPatternPng}
                    alt={"eventbusdesignpattern"}
                    loading="lazy"
                  />
              </ImageListItem>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Overview
              </Typography>
              <Typography variant="body1" align="left">
                <b>Design Patterns</b> help us to build <b>robust, scalable, easy to maintain</b> backend services allowing us flexibility to support various needs of the product being built.
                <br/>In this article we will cover one of the commonly used design pattern i.e <b>Event bus (Publisher-Subscriber)</b> used in Javascript backend services.
                This article is purely based on my experience in the software industry and there would be difference of opinions around using design patterns in microservices.
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                What are design patterns?
              </Typography>
              <Typography variant="body1" align="left">
                Design pattern are <b>Ideas/Concepts</b> that help create code standard template to use throughout our codebase.
                this standard can comprise of how data flows between two microservices, how a microservice is layered to handle api requests,  business logic isolation, how functionality is abstrated away when integrating with databases/helpers/third party endpoints.
                
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Why design patterns?
              </Typography>
              <Typography variant="body1" align="left">
                The idea revolves around repeating same core standard/data flow practises throught our backend services giving us flexibility of Abstraction, enabling us to work with cross team microservices with ease and easy onboarding for new developers.
                also migrations become smoother when we use standards throughtout our codebase.
                <br/>
                Design Patterns are split into <b>Behavioural, Structural and Creational</b> Patterns, In this article we cover the Event Bus Design Pattern which falls under the behavioural design Pattern bucket
                <br/>
                <br/>
                <Alert variant="filled" severity="warning">
                  Please evaluate the individual needs of your product before choosing a specific design pattern. 
                  as design patterns are purely concepts we can have multiple design patterns incorprated within microservices.
                  ofcourse we cant just mash all the design patterns together to get a cocktail of design patterns and expect to have highest efficiency,
                  the choice of design pattern also depends on the needs of the product we are building and we should evaluate which design pattern suits our system best before incorporating it.
                  Its never too late to introduce design patterns in systems.
                </Alert>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Event Bus design pattern
              </Typography>
              <Typography variant="body1" align="left">
                Communication between microservices happens via two approaches:
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    Internal api call between microservices. to cater to syncronous operations.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    <b>Events</b>
                    <br/>
                    This is where our event bus design pattern applies.<br/>
                    When data needs to be processed asynchronously, One service publishes to a topic and another subscribers to the same topic to receive Event with data and then perform operations accordingly.
                    <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '0vw'} }}>
                      <img
                          srcSet={eventBusDesignPatternPng}
                          alt={"microservices"}
                          loading="lazy"
                        />
                    </ImageListItem>
                    <br/>
                    Let us try to get a better understanding of the above diagram with an example using Amazon SNS(Simple Notification Service) topic mechanism
                    <br/>
                    <br/>
                    <b>The Publisher Service: Service 01</b>
                    <br/><br/>
                    <CodeBlock
                      text={codeTextPublish}
                      language={'javascript'}
                      showLineNumbers={true}
                      theme={dracula}
                    />
                    <br/>
                    <b>The Subscriber Service: Service 02</b>
                    <br/><br/>
                    <CodeBlock
                      text={codeTextSubscribe}
                      language={'javascript'}
                      showLineNumbers={true}
                      theme={dracula}
                    />
                    <br/>
                    you can find more information about AWS SSN integration guide <a href="https://docs.aws.amazon.com/sdk-for-javascript/v2/developer-guide/getting-started.html" target="_blank">here</a>
                  </ListItem>
                  </List>
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Fitting Realtime Data Transfer into our Event bus design pattern 
              </Typography>
              <Typography variant="body1" align="left">
                  we saw Amazon SNS as a example of Realtime Messaging System being used to cater to the async flow of service interactions. we could have also used a websocket instead or any other realtime messaging system.
                  hence the point is that just using SNS does not mean Event bus design pattern, we could have use any other mechanism to achieve this realtime event based data transfer.
                  <br/>
                  The highlight is that Event Bus pattern is a Publisher Subscriber mechanism with we do exchange info between services
                  We could also go a step ahead and build us another microservice, lets say <b>Event</b> service, which would listen to a Generic Topic <b>TOPIC_ALL_ORDERS</b> and then based on the data received publish to other topics, this can serve use in complex systems that have interlinked dependencies to have a more concrete centeral event management mechanism.
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Retry Mechanism
              </Typography>
              <Typography variant="body1" align="left">
                  Sometimes the subscriber service can be having issues processing the event, in that case it is always good to have a retry mechanism in place so that the events are not lost
              </Typography>
          </Box>
        </Container>
    )
};